import clsx from 'clsx';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { BsCaretDownFill } from 'react-icons/bs';

import BayAddButton from '../BayAddButton';
import BayTooltip from '../BayTooltip';
import useMediaQuery from '../utilities/hook/useMediaQuery';

import Tabs from './react-responsive-tabs';

import './react-responsive-tabs/styles.scss';
import './BayHeader.scss';

interface NavItem {
	name?: string;
	key?: string | number;
	url?: string;
	disabled?: boolean;
	isComingSoon?: boolean;
	/** Tooltip content for the "Coming Soon" badge, shown below it on hover/focus. */
	comingSoonTooltip?: React.ReactNode;
}

interface ActionChild {
	id: string;
	label: string;
	icon?: React.ReactNode;
	disabled?: boolean;
	toolTipTxt?: string;
	placement?: 'top' | 'bottom' | 'right' | 'left' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';
	className?: string;
}

interface HeaderTitleChild {
	id: string;
	label: string;
	icon?: React.ReactNode;
}

const CustomToggle = React.forwardRef<HTMLDivElement, { children?: React.ReactNode; onClick: (e: React.MouseEvent) => void; className?: string }>(({ children, onClick, className }, ref) => (
	<div
		ref={ref}
		className={clsx('d-flex align-items-center btn btn-outline-secondary more-actions-toggle justify-content-center', className)}
		onClick={(e) => {
			e.preventDefault();
			onClick(e);
		}}
	>
		{children}
		<BsCaretDownFill className="more-actions-toggle-icon ms-1" />
	</div>
));

const CustomHeaderToggle = React.forwardRef<HTMLDivElement, { children?: React.ReactNode; onClick: (e: React.MouseEvent) => void }>(({ children, onClick }, ref) => (
	<div
		ref={ref}
		className="d-flex align-items-center cursor-pointer custom-header-toggle"
		onClick={(e) => {
			e.preventDefault();
			onClick(e);
		}}
	>
		{children}
		<BsCaretDownFill className="more-actions-toggle-icon ms-1" />
	</div>
));

interface BayHeaderProps {
	onActionClick?: (id: string) => void;
	onTitleChildClick?: (id: string) => void;
	onNavItemClick?: (item: NavItem) => void;
	actionChildren?: ActionChild[];
	headerTitleChildren?: HeaderTitleChild[];
	navItems?: NavItem[];
	headerContent?: string | React.ReactNode;
	headerTitle?: string | React.ReactNode;
	primaryActionChild?: React.ReactNode;
	primaryActionOverlay?: string;
	onPrimaryActionClick?: () => void;
	headerImageChild?: React.ReactNode;
	primaryActionText?: string;
	singleRowChild?: string | React.ReactNode;
	disabled?: boolean;
	openSamePage?: boolean;
	isStickyHeader?: boolean;
	isSingleRowContent?: boolean;
	isAnimateOnScroll?: boolean;
	selectedTabKey?: string | number | null;
	linkName?: string;
	linkIcon?: React.ReactNode;
	href?: string;
	primaryActionClassName?: string;
	primaryActionWrapperClassName?: string;
	listingBeta?: boolean;
	noMargin?: boolean;
	titleRightChild?: React.ReactNode;
	/**
	 * Replaces the hardcoded `card border-0 px-5 py-4` className on the
	 * header's inner card wrapper. Bootstrap's `px-5`/`py-4` utilities compile
	 * with `!important` in this build, so a consumer that needs different
	 * padding cannot win with specificity alone -- pass a full replacement
	 * string here (mirroring the default) instead of fighting it with
	 * `!important` from outside.
	 */
	cardClassName?: string;
}

const BayHeader: React.FC<BayHeaderProps> = (props) => {
	const isLarge = useMediaQuery('(min-width: 1281px)');
	const [selectedTitle, setSelectedTitle] = useState('');
	const [selectedAction, setSelectedAction] = useState('');
	const [isScrolled, setIsScrolled] = useState('');
	const [prevScrollPos, setPrevScrollPos] = useState(0);
	const headerRef = useRef<HTMLDivElement>(null);
	const stickyTriggerRef = useRef<HTMLDivElement>(null);

	const {
		onActionClick,
		onTitleChildClick,
		onNavItemClick,
		actionChildren = [],
		headerTitleChildren = [],
		navItems,
		headerContent,
		headerTitle,
		primaryActionChild,
		primaryActionOverlay,
		onPrimaryActionClick,
		headerImageChild,
		primaryActionText,
		singleRowChild,
		disabled = false,
		openSamePage = false,
		isStickyHeader = false,
		isSingleRowContent = false,
		isAnimateOnScroll = false,
		selectedTabKey = null,
		linkName,
		linkIcon = null,
		href,
		primaryActionClassName = '',
		primaryActionWrapperClassName = '',
		listingBeta = false,
		noMargin = false,
		titleRightChild = null,
		cardClassName = 'card border-0 px-5 py-4',
	} = props;

	useEffect(() => {
		if (isAnimateOnScroll) {
			const handleScroll = () => {
				const currentScrollPos = window.scrollY;
				if (currentScrollPos < prevScrollPos && currentScrollPos < 100) {
					setIsScrolled('scrolledUp');
				} else if (currentScrollPos > 100) {
					setIsScrolled('scrolledDown');
				} else {
					setIsScrolled('');
				}
				setPrevScrollPos(currentScrollPos);
			};
			setPrevScrollPos(window.scrollY);
			window.addEventListener('scroll', handleScroll);
			return () => window.removeEventListener('scroll', handleScroll);
		}
	}, [isAnimateOnScroll, prevScrollPos]);

	useEffect(() => {
		if (!isStickyHeader) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (headerRef.current) {
					if (!entry.isIntersecting) {
						headerRef.current.classList.add('is-sticky');
					} else {
						headerRef.current.classList.remove('is-sticky');
					}
				}
			},
			{ threshold: 0 },
		);

		if (stickyTriggerRef.current) {
			observer.observe(stickyTriggerRef.current);
		}

		return () => {
			if (stickyTriggerRef.current) {
				observer.unobserve(stickyTriggerRef.current);
			}
		};
	}, [isStickyHeader]);

	const animateOnScrollClasses = useMemo(() => {
		const animateClasses =
			isScrolled === 'scrolledDown'
				? 'slideUp-animation'
				: isScrolled === 'scrolledUp'
					? 'slideDown-animation'
					: '';
		return `${animateClasses} bay-header-sticky`;
	}, [isScrolled]);

	const navItemChange = (index: number | string) => {
		const item = (navItems ?? [])[index as number] ?? (navItems ?? []).find((item) => item.key === index);
		if (onNavItemClick) {
			onNavItemClick(item);
		} else if (item?.url) {
			if (openSamePage) {
				window.open(item.url, '_self');
			} else {
				window.open(item.url);
			}
		}
	};

	const actionHandler = (id: string) => {
		setSelectedAction(id);
		onActionClick?.(id);
	};

	const titleActionHandler = (event: React.MouseEvent<HTMLElement>) => {
		setSelectedTitle((event.target as HTMLElement).id);
		onTitleChildClick?.((event.target as HTMLElement).id);
	};

	const getTabs = () => {
		return (navItems ?? []).map((item, index) => ({
			title: item?.name ?? 'No title/name set',
			key: item?.key ?? index,
			tabClassName: 'bay-header-nav-links-tabs',
			panelClassName: 'bay-header-nav-links-tab-panels',
			disabled: item?.disabled ?? false,
			isComingSoon: item?.isComingSoon ?? false,
			comingSoonTooltip: item?.comingSoonTooltip,
		}));
	};

	const renderNavItems = () => {
		return (
			<Tabs
				items={getTabs()}
				selectedTabKey={selectedTabKey ?? undefined}
				showMoreLabel={
					<div className="d-flex align-items-center">
						More
						<BsCaretDownFill className="ms-1" />
					</div>
				}
				transform={false}
				onChange={navItemChange}
			/>
		);
	};

	const renderTitleDropDown = () => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomHeaderToggle} variant="outline-secondary">
					<h1 className="header-title-text m-0">{headerTitle}</h1>
				</Dropdown.Toggle>
				<Dropdown.Menu className="px-0" renderOnMount={false}>
					{headerTitleChildren.map((action) => (
						<Dropdown.Item
							key={action.id}
							active={action.id === selectedTitle}
							className="d-flex"
							eventKey={action.id}
							id={action.id}
							onClick={titleActionHandler}
						>
							{action.icon ? <div className="me-1">{action?.icon}</div> : null}
							{action.label}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const renderMoreActions = (actionsMenuArray: ActionChild[], selectedAction: string, actionHandler: (id: string) => void) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle} bsPrefix="more-actions-trigger" variant="outline-secondary" className={clsx('text-nowrap', actionsMenuArray?.map((action) => action?.className))}>
					More Actions
				</Dropdown.Toggle>
				<Dropdown.Menu className="w-100" renderOnMount={false}>
					{actionsMenuArray?.map((action) => (
						<Dropdown.Item
							key={action.id}
							active={action.id === selectedAction}
							className="d-flex"
							disabled={action.disabled ?? false}
							eventKey={action.id}
							id={action.id}
							onClick={() => actionHandler(action.id)}
						>
							{action.icon ? (
								<div className="me-1">{action?.icon}</div>
							) : null}
							<div className="text-wrap">{action.label}</div>
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const renderMultipleActionButtons = () => {
		if (actionChildren.length === 1) {
			return (
				<BayTooltip
					disabled={!(actionChildren?.[0]?.disabled ?? false)}
					overlay={actionChildren[0].toolTipTxt ?? ''}
					overlayMaxWidth="fit-content"
					placement={actionChildren[0].placement ?? 'top'}
				>
					<div className="multiple-action-item cursor-pointer">
						<Button
							className={clsx('d-flex align-items-center justify-content-center', actionChildren[0]?.className)}
							disabled={actionChildren[0].disabled ?? false}
							id={actionChildren[0].id}
							variant="outline-secondary"
							onClick={() => actionHandler(actionChildren[0].id)}
						>
							{actionChildren[0]?.icon ? (
								<div className="me-2">{actionChildren[0]?.icon}</div>
							) : null}
							{actionChildren[0].label}
						</Button>
					</div>
				</BayTooltip>
			);
		}

		let actionsArray = [...actionChildren];

		let actionsMenuArray = [...actionChildren];

		if (actionChildren.length > 3 && isLarge) {
			actionsArray = [...actionChildren].slice(0, 2);
			actionsMenuArray = [...actionChildren].slice(2);
		}

		return (
			<>
				{actionsArray.length < 4 &&
					actionsArray.map((action, index) => {
						return (
							<div
								key={index}
								className="d-none d-xl-block multiple-action-item"
							>
								<Button
									key={index}
									className={clsx('d-flex align-items-center justify-content-center', action?.className)}
									disabled={action.disabled ?? false}
									id={action.id}
									variant="outline-secondary"
									onClick={() => actionHandler(action.id)}
								>
									{action.icon ? (
										<div key={index} className="me-2">
											{action?.icon}
										</div>
									) : null}
									{action.label}
								</Button>
							</div>
						);
					})}
				{actionChildren.length > 3 && (
					<div className="more-action-wrapper">
						{renderMoreActions(actionsMenuArray, selectedAction, actionHandler)}
					</div>
				)}
			</>
		);
	};

	const renderPrimaryAction = () => {
		return primaryActionChild ? (
			primaryActionChild
		) : primaryActionText ? (
			<BayAddButton
				className={primaryActionClassName}
				disabled={disabled}
				overlay={primaryActionOverlay}
				showTooltip={(primaryActionOverlay?.length ?? 0) > 0}
				text={primaryActionText}
				onClick={onPrimaryActionClick}
			/>
		) : null;
	};

	const renderHeaderContent = () => {
		return (
			<div className="d-none d-sm-block header-content col">
				<div className="row g-0 bay-page-header-body">
					<div className="col-12 text-700 header-content-text">
						{headerContent}
					</div>
				</div>
			</div>
		);
	};

	const renderActions = () => {
		const primaryActionClasses = clsx(
			'primary-action-child-wrapper',
			primaryActionWrapperClassName,
		);

		return (
			<div className="col-12 col-md-auto mt-3 mt-md-0">
				<div className="d-flex flex-wrap justify-content-sm-start justify-content-lg-end action-buttons-container">
					<div className={primaryActionClasses}>
						{renderPrimaryAction()}
					</div>
					{actionChildren?.length > 0 && renderMultipleActionButtons()}
				</div>
			</div>
		);
	};

	return (
		<>
			<div
				ref={stickyTriggerRef}
				className="invisible-sticky-trigger"
				style={{ height: '1px' }}
			/>{' '}
			{/* we use intersection observer to detect when the header is sticky */}
			<div
				ref={headerRef}
				className={`bay-header ${isStickyHeader ? 'bay-header-sticky' : ''}  ${isAnimateOnScroll ? animateOnScrollClasses : ''}`}
			>
				<div className={`${cardClassName}${noMargin ? ' mb-0' : ''}`}>
					<div className="d-flex gap-4">
						{headerImageChild && (
							<div className="image-holder d-none d-sm-block">
								{headerImageChild}
							</div>
						)}
						{isSingleRowContent ? (
							<div className="single-row-content w-100">
								{singleRowChild ? singleRowChild : null}
							</div>
						) : (
							<div className="bay-header-content-wrapper">
								<div className="row g-0">
									<div className="col-12 mb-2">
										<div className="d-flex justify-content-between">
											{!headerTitleChildren?.length ? (
												<h1 className="header-title-text m-0">{headerTitle}</h1>
											) : (
												renderTitleDropDown()
											)}
											{(titleRightChild || linkName) && (
												<div className="d-flex align-items-center gap-2">
													{titleRightChild && (
														<div className="title-right-child">{titleRightChild}</div>
													)}
													{linkName && (
														<div className="help_link gap-2 text-primary mt-1">
															<a className="text-decoration-none" href={href} rel="noopener noreferrer" target="_blank">
																<span className="cursor-pointer text-primary">
																	{linkName}
																</span>
															</a>
															{!listingBeta && (linkIcon ?? '?')}
														</div>
													)}
												</div>
											)}
										</div>
										{linkName && (
											<div className="visible_help_link d-sm-none gap-2 text-primary mt-4">
												<a className="text-decoration-none" href={href} rel="noopener noreferrer" target="_blank">
													<span className="cursor-pointer text-primary">
														{linkName}
													</span>
												</a>
												{!listingBeta && (linkIcon ?? '?')}
											</div>
										)}
									</div>
								</div>
								{navItems?.length && navItems.length > 0 && (
									<div className={`row g-0 ${!headerImageChild && 'mb-3'}`}>
										{renderNavItems()}
									</div>
								)}
								{!navItems && (
									<div className="row">
										{renderHeaderContent()}
										{renderActions()}
									</div>
								)}
							</div>
						)}
					</div>
					{navItems && (
						<div className={`row ${headerImageChild && 'mt-4'}`}>
							{renderHeaderContent()}
							{renderActions()}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default BayHeader;
